import { useEffect, useState } from 'react';
import { Search, FileText, Download, Trash2, Replace, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { getWorkspaceDocuments, getDocumentDownloadUrl, deleteDocument } from '@/lib/database';
import type { DocumentWithProject } from '@/lib/database';
import { useReplaceDocument, ACCEPTED_DOCUMENT_TYPES } from '@/hooks/useReplaceDocument';
import { useWorkspace } from '@/store/workspace';
import { format } from 'date-fns';

const documentTypeLabels: Record<string, string> = {
  meeting_notes: 'Meeting Notes',
  email_thread: 'Email Thread',
  slack_export: 'Slack Export',
  project_plan: 'Project Plan',
  status_report: 'Status Report',
  other: 'Other',
};

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentList() {
  const { currentWorkspace, canEdit } = useWorkspace();
  const [documents, setDocuments] = useState<DocumentWithProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Shared replace-file interaction (same hook the project page uses). Merge
  // the returned row into existing items so the joined project_name is kept.
  const {
    replacingId,
    inputRef: replaceInputRef,
    startReplace,
    onFileSelected,
  } = useReplaceDocument((updated) =>
    setDocuments((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)))
  );

  useEffect(() => {
    loadDocuments();
  }, [currentWorkspace]);

  const loadDocuments = async () => {
    if (!currentWorkspace) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await getWorkspaceDocuments(currentWorkspace.id);
      setDocuments(data);
      // Expand all projects by default
      const projectNames = new Set(data.map(d => d.project_name));
      setExpandedProjects(projectNames);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc: DocumentWithProject) => {
    try {
      setDownloadingId(doc.id);
      const url = await getDocumentDownloadUrl(doc.file_path);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to download document:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (doc: DocumentWithProject) => {
    if (!confirm(`Delete "${doc.original_name}"? This cannot be undone.`)) return;
    try {
      setDeletingId(doc.id);
      await deleteDocument(doc.id);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (error) {
      console.error('Failed to delete document:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const toggleProject = (projectName: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectName)) {
        next.delete(projectName);
      } else {
        next.add(projectName);
      }
      return next;
    });
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc =>
    doc.original_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.project_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group by project
  const groupedByProject = filteredDocuments.reduce<Record<string, DocumentWithProject[]>>((acc, doc) => {
    if (!acc[doc.project_name]) acc[doc.project_name] = [];
    acc[doc.project_name].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No workspace selected</h3>
        <p className="text-gray-600">Please select or create a workspace first</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Shared hidden input for Replace; one per page, target row tracked by the hook. */}
      <input
        ref={replaceInputRef}
        type="file"
        accept={ACCEPTED_DOCUMENT_TYPES}
        onChange={onFileSelected}
        className="hidden"
      />

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
        <p className="text-gray-600 mt-1">
          Browse and manage documents across all projects
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          placeholder="Search documents..."
          className="pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Document Groups */}
      {Object.keys(groupedByProject).length > 0 ? (
        <div className="space-y-4">
          {Object.entries(groupedByProject).map(([projectName, docs]) => (
            <Card key={projectName}>
              <button
                onClick={() => toggleProject(projectName)}
                className="w-full flex items-center gap-2 px-6 py-4 text-left hover:bg-gray-50 transition-colors"
              >
                {expandedProjects.has(projectName) ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
                <h3 className="font-semibold text-gray-900">{projectName}</h3>
                <span className="text-sm text-gray-500">({docs.length})</span>
              </button>

              {expandedProjects.has(projectName) && (
                <CardContent className="pt-0">
                  <div className="divide-y">
                    {docs.map(doc => (
                      <div key={doc.id} className="flex items-center gap-4 py-3">
                        <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {doc.original_name}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                              {documentTypeLabels[doc.type] || doc.type}
                            </span>
                            <span>{formatFileSize(doc.file_size)}</span>
                            {doc.uploaded_at && (
                              <span>{format(new Date(doc.uploaded_at), 'MMM dd, yyyy')}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownload(doc)}
                            disabled={downloadingId === doc.id}
                            title="Download"
                          >
                            {downloadingId === doc.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                          {canEdit() && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => startReplace(doc.id)}
                              disabled={replacingId === doc.id}
                              title="Replace"
                            >
                              {replacingId === doc.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Replace className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {canEdit() && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(doc)}
                              disabled={deletingId === doc.id}
                              title="Delete"
                              className="text-gray-400 hover:text-red-500"
                            >
                              {deletingId === doc.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-2 border-dashed border-gray-300 bg-gray-50/50">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-gray-100 p-3 mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchQuery ? 'No documents found' : 'No documents yet'}
            </h3>
            <p className="text-gray-600 mb-6 max-w-sm text-center">
              {searchQuery
                ? `No documents match "${searchQuery}". Try adjusting your search.`
                : 'Documents uploaded to projects will appear here. Go to a project to upload documents.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
